import {
  formatDuration,
  type ActivityRow,
  type LeaderboardRow,
  type PlanogramReportRow,
  type PriceIndexRegionRow,
  type PriceIndexRow,
  type GpsValidation,
} from '@fieldvision/contracts';
import {
  Camera,
  ChartColumn,
  ChartLine,
  ClipboardCheck,
  Crosshair,
  Grid3x3,
  Radio,
  Route,
  ShieldAlert,
  Store,
  Timer,
  Trophy,
} from 'lucide-react';
import { lazy, useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  Badge,
  ChartFrame,
  EmptyState,
  FilterBar,
  MiniTable,
  Panel,
  PanelHeader,
  PanelLink,
  QueryError,
  Stat,
  type MiniColumn,
  type StatDelta,
} from '../../components/ui/index.js';
import { compact, decimal, integer, percent, rupiah } from '../../lib/format.js';
import { GPS_LABEL, GPS_TONE } from '../../lib/gps-verdict.js';
import { attainmentTone } from '../../lib/thresholds.js';
import { useInViewOnce } from '../../lib/use-in-view.js';
import { FlaggedPanel } from './FlaggedPanel.js';
import { MasterDataPanel } from './MasterDataPanel.js';
import { PlanVsActualPanel } from './PlanVsActualPanel.js';
import { useOverviewData } from './use-overview-data.js';

/*
 * Recharts is loaded on demand.
 *
 * These two components live on the landing route, so route-level splitting does
 * nothing for them. Lazy-loading here means the chart library is fetched in
 * PARALLEL with, and does not block, the KPI row and the tables — the numbers
 * paint first, which is what a manager opening this screen is actually after.
 */
const VisitTrendChart = lazy(() => import('./VisitTrendChart.js'));
const ChannelScoreChart = lazy(() => import('./ChannelScoreChart.js'));

/**
 * FR-14 Manager Dashboard.
 *
 * THE WINDOW USED TO BE FIXED AT 30 DAYS HERE, on the argument that this is the
 * "what is happening now" screen and that choosing a range is what `/reports` is
 * for. That was wrong about what the screen is. Every panel below is a summary
 * over a period, and a period a manager cannot change is a period they cannot
 * reconcile against a month-end number.
 *
 * The filter is now here, and it is the SAME store `/reports` and `/price-index`
 * read. That is the load-bearing part: every panel links out to the screen that
 * owns its data, and a deep link that lands on a different window than the
 * number it was clicked from would make the whole summary-plus-link shape a lie.
 *
 * THE ACTIVITY MONITOR IS THE EXCEPTION and sits ABOVE the filter. It answers
 * "who is in a store right now" — `/reporting/activity` accepts no date
 * parameters at all — and a live visit rendered inside a July frame reads as a
 * July visit. It says so on the panel.
 *
 * It also refreshes on a timer, and it is the ONLY query on this page that does.
 * TSP 4.3 predicts this screen contending with report generation on the same
 * database as the likely first production incident, which is why the interval is
 * 30 seconds rather than the 5 a dashboard designer would reach for — and why
 * the other ten queries poll not at all. See `use-overview-data.ts` for the rest
 * of the request budget.
 */
export function OverviewPage() {
  const [foldRef, belowFold] = useInViewOnce<HTMLDivElement>();
  const data = useOverviewData({ belowFold });
  const {
    enabled,
    previousLabel,
    activity,
    summary,
    previousSummary,
    planVsActual,
    trend,
    leaderboard,
    planogramByChannel,
    worstOutlets,
    priceByRegion,
    priceBySku,
    flags,
  } = data;

  const stats = summary.data;
  const previous = previousSummary.data;

  const activityColumns = useMemo<MiniColumn<ActivityRow>[]>(
    () => [
      { key: 'sales', header: 'Petugas', mobileTitle: true, render: (row) => row.salesUserName },
      { key: 'outlet', header: 'Outlet', render: (row) => row.outletName },
      { key: 'region', header: 'Distributor', mobileHidden: true, render: (row) => row.regionName },
      { key: 'photos', header: 'Foto', align: 'right', render: (row) => integer(row.photoCount) },
      {
        key: 'elapsed',
        header: 'Durasi',
        align: 'right',
        /*
         * `formatDuration` takes SECONDS and this row carries minutes, but the
         * multiplication invents no precision: the api computes the field as
         * `round(extract(epoch …) / 60)`, so it is already minute-granular
         * before it reaches the wire. It stays minutes there because
         * `minutesElapsed` is an `ActivitySortKey` and this table's default
         * sort — renaming it would 400 an older console against a newer api.
         */
        render: (row) => formatDuration(row.minutesElapsed * 60),
      },
      {
        key: 'checkin',
        header: 'Check-in',
        // Same table as the reports list, and for the same reason — see
        // `lib/gps-verdict.ts`. "Valid" is a claim, not a default.
        render: (row) => {
          const validation = row.checkInValidation as GpsValidation;
          return <Badge tone={GPS_TONE[validation]}>{GPS_LABEL[validation]}</Badge>;
        },
      },
    ],
    [],
  );

  const leaderboardColumns = useMemo<MiniColumn<LeaderboardRow>[]>(
    () => [
      { key: 'sales', header: 'Petugas', mobileTitle: true, render: (row) => row.salesUserName },
      { key: 'region', header: 'Distributor', mobileHidden: true, render: (row) => row.regionName },
      // COVERAGE, not check-ins, because of the badge sitting next to it. The
      // target percentage is computed from distinct outlets; showing raw visits
      // beside it would invite a reader to divide the two numbers they can see
      // and get a third that disagrees with the badge. The full check-in count
      // is on /reports, where there is room for both.
      {
        key: 'outletsVisited',
        header: 'Outlet',
        align: 'right',
        render: (row) => integer(row.outletsVisited),
      },
      {
        key: 'attainment',
        header: 'Target',
        align: 'right',
        render: (row) => {
          const tone = attainmentTone(row.attainmentPct);
          if (tone === null) return <span className="text-muted">—</span>;
          return <Badge tone={tone}>{percent(row.attainmentPct)}</Badge>;
        },
      },
      {
        key: 'score',
        header: 'Skor',
        align: 'right',
        render: (row) => decimal(row.averagePlanogramScore, 1),
      },
    ],
    [],
  );

  const worstColumns = useMemo<MiniColumn<PlanogramReportRow>[]>(
    () => [
      { key: 'outlet', header: 'Outlet', mobileTitle: true, render: (row) => row.outletName },
      { key: 'channel', header: 'Channel', mobileHidden: true, render: (row) => row.channel },
      { key: 'photos', header: 'Foto', align: 'right', render: (row) => integer(row.scoredPhotos) },
      {
        key: 'score',
        header: 'Skor',
        align: 'right',
        render: (row) => {
          /*
           * A PLAIN NUMBER. This is an outlet mean over photos and has no rank —
           * a DIAMOND photo and a COAL photo mean to SILVER, a band neither held.
           * The panel is already sorted worst-first, so the colour was carrying
           * no information the ordering did not.
           */
          if (row.averageScore === null) return <span className="text-muted">—</span>;
          return <span className="tabular-nums">{decimal(row.averageScore, 1)}</span>;
        },
      },
    ],
    [],
  );

  const regionPriceColumns = useMemo<MiniColumn<PriceIndexRegionRow>[]>(
    () => [
      { key: 'region', header: 'Distributor', mobileTitle: true, render: (row) => row.regionName },
      { key: 'sku', header: 'SKU', render: (row) => row.skuName },
      { key: 'avg', header: 'Rata-rata', align: 'right', render: (row) => rupiah(row.averageIdr) },
    ],
    [],
  );

  const skuPriceColumns = useMemo<MiniColumn<PriceIndexRow>[]>(
    () => [
      { key: 'sku', header: 'SKU', mobileTitle: true, render: (row) => row.skuName },
      {
        key: 'type',
        header: 'Tipe',
        mobileHidden: true,
        render: (row) => <Badge tone={row.skuType === 'INTERNAL' ? 'info' : 'neutral'}>{row.skuType}</Badge>,
      },
      { key: 'obs', header: 'Observasi', align: 'right', render: (row) => integer(row.observations) },
      { key: 'avg', header: 'Rata-rata', align: 'right', render: (row) => rupiah(row.averageIdr) },
    ],
    [],
  );

  const trendRows = trend.data ?? [];
  const channelRows = planogramByChannel.data?.data ?? [];

  return (
    <div className="flex flex-col gap-section">
      <header>
        <p className="eyebrow">Pantauan</p>
        <h1 className="mt-1 text-2xl">Ringkasan</h1>
        <p className="mt-1 text-sm text-muted">
          Activity Monitor berjalan real-time; panel lainnya mengikuti rentang tanggal di bawah.
        </p>
      </header>

      {/* Row 0 — above the filter, because it is not filtered. */}
      <Panel className="p-0">
        <div className="p-5 pb-0">
          <div className="flex items-start gap-2.5">
            <Radio size={18} className="mt-1 shrink-0 text-steel" aria-hidden />
            <div className="min-w-0 flex-1">
              <PanelHeader
                title="Activity Monitor"
                description="Kunjungan yang sudah check-in dan belum check-out. Diperbarui setiap 30 detik, tidak terpengaruh rentang tanggal."
                action={<Badge tone="info">Saat ini</Badge>}
              />
            </div>
          </div>
        </div>
        <MiniTable
          rows={activity.data?.data}
          columns={activityColumns}
          rowKey={(row) => row.visitId}
          isLoading={activity.isPending}
          empty={
            <EmptyState
              title="Tidak ada kunjungan aktif"
              description="Kunjungan yang sedang berjalan akan muncul di sini."
              icon={Radio}
            />
          }
          {...((activity.data?.total ?? 0) > 8
            ? { footer: `Menampilkan 8 dari ${integer(activity.data?.total ?? 0)} kunjungan aktif.` }
            : {})}
        />
      </Panel>

      {/* Row 1 — the window every panel below shares with /reports. */}
      <FilterBar fields={['region', 'channel']} layout="row" />

      {/* `QueryError` rather than the copy of `ChartFrame`'s branch that used to
          live here — same shape, and it shows the server's message instead of
          discarding it. */}
      {summary.isError ? (
        <QueryError what="ringkasan" error={summary.error} onRetry={() => void summary.refetch()} />
      ) : null}

      {/* Row 2 — the KPI row. */}
      <section className={`grid grid-cols-2 gap-stack transition-opacity duration-150 sm:grid-cols-3 lg:grid-cols-6 ${summary.isFetching && !summary.isPending ? 'opacity-60' : ''}`}>
        <Stat
          label="Kunjungan"
          value={compact(stats?.visits ?? 0)}
          title={integer(stats?.visits ?? 0)}
          icon={Route}
          to="/reports"
          {...deltaProps(stats?.visits, previous?.visits, 'up', previousLabel)}
        />
        <Stat
          label="Selesai"
          value={compact(stats?.closedVisits ?? 0)}
          title={integer(stats?.closedVisits ?? 0)}
          icon={ClipboardCheck}
          to="/reports"
          {...deltaProps(stats?.closedVisits, previous?.closedVisits, 'up', previousLabel)}
        />
        <Stat
          label="Durasi rata-rata"
          value={formatDuration(stats?.averageDurationSeconds)}
          icon={Timer}
        />
        <Stat
          label="Foto"
          value={compact(stats?.photos ?? 0)}
          title={integer(stats?.photos ?? 0)}
          icon={Camera}
          {...deltaProps(stats?.photos, previous?.photos, 'up', previousLabel)}
        />
        <Stat
          label="Di luar toleransi"
          value={compact(stats?.outOfTolerance ?? 0)}
          title={integer(stats?.outOfTolerance ?? 0)}
          icon={Crosshair}
          tone={(stats?.outOfTolerance ?? 0) > 0 ? 'warn' : 'default'}
          to="/reports"
          {...deltaProps(stats?.outOfTolerance, previous?.outOfTolerance, 'down', previousLabel)}
        />
        <Stat
          label="Terflag"
          value={compact(stats?.flagged ?? 0)}
          title={integer(stats?.flagged ?? 0)}
          icon={ShieldAlert}
          tone={(stats?.flagged ?? 0) > 0 ? 'warn' : 'default'}
          to="/fraud"
          {...deltaProps(stats?.flagged, previous?.flagged, 'down', previousLabel)}
        />
      </section>

      {/*
        Say what these numbers leave out.

        Every tile above excludes visits a supervisor rejected, and `fraud:read`
        is a narrower audience than `dashboard:read` — so most people reading a
        total that dropped cannot see the flag that dropped it. Rejections also
        carry no time limit, which means a figure exported in September can
        legitimately disagree with the same figure re-read in November. One line
        naming the exclusion turns "this looks wrong" into something checkable.
      */}
      {(stats?.excluded?.visits ?? 0) > 0 ? (
        <p className="text-sm text-muted">
          {integer(stats?.excluded?.visits ?? 0)} kunjungan ditolak dikecualikan dari seluruh angka
          di halaman ini
          {(stats?.excluded?.photos ?? 0) > 0
            ? `, termasuk ${integer(stats?.excluded?.photos ?? 0)} foto`
            : ''}
          . Daftarnya tetap ada di <Link className="underline" to="/reports">Laporan</Link>.
        </p>
      ) : null}

      {/* Row 3 — Plan vs Actual │ trend. */}
      <section className="grid min-w-0 gap-stack lg:grid-cols-2">
        <div className="min-w-0">
          <PlanVsActualPanel
            data={planVsActual.data}
            isLoading={planVsActual.isPending && enabled}
            isFetching={planVsActual.isFetching}
            disabled={!enabled}
          />
        </div>
        <div className="min-w-0">
          <ChartFrame
            title="Tren kunjungan"
            description="Kunjungan per hari pada rentang terpilih."
            icon={ChartColumn}
            to="/reports"
            isLoading={trend.isPending && enabled}
            isFetching={trend.isFetching}
            isError={trend.isError}
            error={trend.error}
            onRetry={() => void trend.refetch()}
            disabled={!enabled}
            isEmpty={trendRows.length === 0}
            empty={<EmptyState title="Tidak ada kunjungan pada rentang ini" icon={ChartColumn} />}
            table={{
              caption: 'Tren kunjungan harian',
              head: ['Tanggal', 'Kunjungan', 'Selesai'],
              rows: trendRows.map((point) => [
                point.day,
                integer(point.visits),
                integer(point.closedVisits),
              ]),
            }}
          >
            <VisitTrendChart data={trendRows} />
          </ChartFrame>
        </div>
      </section>

      {/* The fold. Everything below fetches only once scrolled near. */}
      <div ref={foldRef} />

      {/* Row 4 — leaderboard │ planogram per channel. */}
      <section className="grid min-w-0 gap-stack lg:grid-cols-2">
        <div className="min-w-0">
          <Panel className="flex h-full flex-col p-0">
            <div className="p-5 pb-0">
              <div className="flex items-start gap-2.5">
                <Trophy size={18} className="mt-1 shrink-0 text-steel" aria-hidden />
                <div className="min-w-0 flex-1">
                  <PanelHeader
                    title="Peringkat Petugas"
                    description="Lima teratas berdasarkan jumlah kunjungan."
                    action={<PanelLink to="/reports?tab=leaderboard" />}
                  />
                </div>
              </div>
            </div>
            <MiniTable
              rows={leaderboard.data?.data}
              columns={leaderboardColumns}
              rowKey={(row) => row.salesUserId}
              isLoading={leaderboard.isPending && enabled && belowFold}
              empty={
                <EmptyState
                  title="Belum ada data peringkat"
                  description="Tidak ada kunjungan pada rentang dan filter ini."
                  icon={Trophy}
                />
              }
            />
          </Panel>
        </div>

        <div className="min-w-0">
          <ChartFrame
            title="Skor Planogram per Channel"
            description="Rata-rata skor kepatuhan, dibandingkan target."
            icon={ChartColumn}
            to="/reports?tab=planogram"
            isLoading={planogramByChannel.isPending && enabled && belowFold}
            isFetching={planogramByChannel.isFetching}
            isError={planogramByChannel.isError}
            error={planogramByChannel.error}
            onRetry={() => void planogramByChannel.refetch()}
            disabled={!enabled}
            isEmpty={channelRows.length === 0}
            empty={
              <EmptyState
                title="Belum ada foto yang dinilai"
                description="Kepatuhan planogram muncul setelah foto rak dinilai."
                icon={Grid3x3}
              />
            }
            table={{
              caption: 'Skor planogram rata-rata per Channel',
              head: ['Channel', 'Outlet', 'Foto dinilai', 'Skor rata-rata'],
              rows: channelRows.map((row) => [
                row.channel,
                integer(row.outlets),
                integer(row.scoredPhotos),
                decimal(row.averageScore, 1),
              ]),
            }}
          >
            <ChannelScoreChart data={channelRows} />
          </ChartFrame>
        </div>
      </section>

      {/* Row 5 — price index │ worst outlets. */}
      <section className="grid min-w-0 gap-stack lg:grid-cols-2">
        <div className="min-w-0">
          <Panel className="flex h-full flex-col p-0">
            <div className="p-5 pb-0">
              <div className="flex items-start gap-2.5">
                <ChartLine size={18} className="mt-1 shrink-0 text-steel" aria-hidden />
                <div className="min-w-0 flex-1">
                  <PanelHeader
                    title="Harga Pasar"
                    description="Rata-rata harga yang terpantau di rak."
                    action={<PanelLink to="/price-index" />}
                  />
                </div>
              </div>
            </div>

            <div className="px-5 pb-2">
              <p className="eyebrow">Per Distributor</p>
            </div>
            <MiniTable
              rows={priceByRegion.data?.data}
              columns={regionPriceColumns}
              rowKey={(row) => `${row.regionId}-${row.skuId}`}
              isLoading={priceByRegion.isPending && enabled && belowFold}
              empty={<EmptyState title="Belum ada observasi harga" icon={ChartLine} />}
            />

            <div className="px-5 pb-2 pt-4">
              <p className="eyebrow">Per SKU</p>
            </div>
            <MiniTable
              rows={priceBySku.data?.data}
              columns={skuPriceColumns}
              rowKey={(row) => row.skuId}
              isLoading={priceBySku.isPending && enabled && belowFold}
              empty={<EmptyState title="Belum ada observasi harga" icon={ChartLine} />}
            />
          </Panel>
        </div>

        <div className="min-w-0">
          <Panel className="flex h-full flex-col p-0">
            <div className="p-5 pb-0">
              <div className="flex items-start gap-2.5">
                <Store size={18} className="mt-1 shrink-0 text-steel" aria-hidden />
                <div className="min-w-0 flex-1">
                  {/*
                    The header says "terendah" explicitly. This list is sorted
                    ascending because the bottom is the actionable end — but a
                    five-row table under a neutral title reads as a top five, and
                    a manager congratulating the worst outlets is a bad afternoon.
                  */}
                  <PanelHeader
                    title="Skor Planogram per Outlet"
                    description="Lima Outlet dengan skor terendah — yang perlu ditindaklanjuti."
                    action={<PanelLink to="/reports?tab=planogram" />}
                  />
                </div>
              </div>
            </div>
            <MiniTable
              rows={worstOutlets.data?.data}
              columns={worstColumns}
              rowKey={(row) => row.outletId}
              isLoading={worstOutlets.isPending && enabled && belowFold}
              empty={
                <EmptyState
                  title="Belum ada foto yang dinilai"
                  description="Kepatuhan planogram muncul setelah foto rak dinilai."
                  icon={Grid3x3}
                />
              }
            />
          </Panel>
        </div>
      </section>

      {/* Row 6 — flagged visits │ master data. */}
      <section className="grid min-w-0 gap-stack lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <FlaggedPanel
            data={flags.data}
            isLoading={flags.isPending && enabled && belowFold}
            isFetching={flags.isFetching}
            disabled={!enabled}
          />
        </div>
        <div className="min-w-0">
          <MasterDataPanel />
        </div>
      </section>

      {/*
        `total` counts rows matching the filters AND the caller's row-level
        scope, so two managers legitimately see different numbers for the same
        query. Saying so once beats explaining it in a support thread.
      */}
      <p className="text-sm text-muted">
        Angka mengikuti Distributor yang menjadi tanggung jawab Anda.
      </p>
    </div>
  );
}

/**
 * The KPI delta, or nothing.
 *
 * Returns no `delta` prop at all when there is no previous figure, rather than a
 * zero: "unchanged" and "we have nothing to compare against" look identical as
 * 0% and mean different things.
 */
function deltaProps(
  current: number | undefined,
  previous: number | undefined,
  goodWhen: 'up' | 'down',
  label: string,
): { delta?: StatDelta } {
  if (current === undefined || previous === undefined) return {};
  if (previous === 0) return {};

  const change = ((current - previous) / previous) * 100;
  if (!Number.isFinite(change)) return {};

  const direction = Math.abs(change) < 0.5 ? 'flat' : change > 0 ? 'up' : 'down';
  const sign = change > 0 ? '+' : '';

  return {
    delta: {
      value: `${sign}${decimal(change, 1)}%`,
      direction,
      goodWhen,
      label,
    },
  };
}
